import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { FinesService } from '../../service/main/fines.service';
import { DateRange, Supervisor, Check } from '../../entity';
import { BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import { PaginationService } from '../../service';

@Component({
    selector: 'app-supervisor-checked-cars',
    templateUrl: './supervisor-checked-cars.component.html'
})

export class SupervisorCheckedCarsComponent implements OnInit {
    supervisor: Supervisor = new Supervisor();
    vehicleCkecks: Array<Check> = new Array<Check>();
    supervisorLocations: Array<Check> = new Array<Check>();
    filteredList: Array<Check> = new Array<Check>();

    //#region component state properties
    loadingState = false;
    noRecordsState = true;
    //#endregion

    //#region date picker properties
    initialPickerDate: Date[];
    bsConfig: Partial<BsDaterangepickerConfig> = {
        rangeInputFormat: 'DD/MM/YYYY',
        showWeekNumbers: false
    };
    days: Array<string>;
    selectedDay: string;
    //#endregion

    //#region search & sort properties
    tags = new Array<string>();
    searchWord = '';
    //#endregion

    //#region pagination properties
    pagedItems: Array<Check>;
    pager: any = {};
    //#endregion

    //#region map properties
    latitude = 44.426652;
    longitude = 26.104022;
    zoom = 15;
    //#endregion

    constructor(
        private route: ActivatedRoute,
        private finesService: FinesService,
        private paginationService: PaginationService
    ) { }

    ngOnInit() {
        this.refresh();
    }

    //#region Methods triggered on init
    refresh() {
        this.route.queryParams.subscribe((params: Params) => {
            this.supervisor.FullName = params['name'];
            this.supervisor.PortalOperatorID = params['id'];
            this.onDateRangeChanged([new Date(), new Date()]);
        });
    }

    getCheckedVehicles(dateRange: DateRange) {
        this.loadingState = true;
        this.noRecordsState = false;

        this.finesService.getCheckedVehiclesbySupervisor(dateRange).subscribe(data => {
            const vehicleCkecks = new Array<Check>();
            data.forEach(item => {
                const vehicleCheck = new Check();
                vehicleCheck.fromObject(item);

                if (vehicleCheck.IsValid === null) {
                    vehicleCheck.IsValid = false;
                }

                vehicleCkecks.push(vehicleCheck);
            });

            this.vehicleCkecks = vehicleCkecks;
            this.getDaysList(vehicleCkecks);
            this.filteredList = vehicleCkecks;
            this.loadingState = false;
            this.setPage(1);

            if (this.vehicleCkecks.length > 0) {
                this.noRecordsState = false;
            } else {
                this.noRecordsState = true;
            }
        }, (err) => {
            this.loadingState = false;
            this.noRecordsState = true;
        });
    }

    getSupervisorLocations(date: Date) {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        this.supervisorLocations = new Array<Check>();

        this.vehicleCkecks.forEach(item => {
            const checkDate = new Date(item.Date);
            checkDate.setHours(0);
            checkDate.setMinutes(0);
            checkDate.setSeconds(0);
            checkDate.setMilliseconds(0);

            if (checkDate.getTime() === date.getTime()) {
                this.supervisorLocations.push(item);
            }
        });

        this.setMapPosition(this.supervisorLocations);
    }

    getDaysList(vehicleCkecks: Array<Check>) {
        const days = new Array<Date>();
        this.days = new Array<string>();

        //#region Get list of dates
        for (const check of vehicleCkecks) {
            const date = new Date(check.Date);

            const index = days.findIndex(item => (
                item.getDate() === date.getDate() &&
                item.getMonth() === date.getMonth() &&
                item.getFullYear() === date.getFullYear())
            );

            if (index < 0) {
                days.push(date);
            }
        }
        //#endregion

        //#region Parse dates into strings
        for (const day of days) {
            this.days.push(`${day.getDate()}-${day.getMonth() + 1}-${day.getFullYear()}`);
        }
        //#endregion

        //#region get supervisor location by last date
        if (this.days.length > 0) {
            const lastDay = this.days[0];

            this.onSelectDate(lastDay);
        }
        //#endregion
    }
    //#endregion

    //#region Methods triggered by DOM
    onDateRangeChanged(event: Date[]) {
        const dateRange = new DateRange(
            event[0],
            event[1],
            this.supervisor.PortalOperatorID
        );
        dateRange.StartDate.setHours(2);
        dateRange.StartDate.setMinutes(0);

        dateRange.EndDate.setHours(25);
        dateRange.EndDate.setMinutes(59);

        this.getCheckedVehicles(dateRange);
    }

    onSelectDate(date: string) {
        const day = new Date();
        day.setDate(Number(date.split('-')[0]));
        day.setMonth(Number(date.split('-')[1]) - 1);
        day.setFullYear(Number(date.split('-')[2]));

        this.getSupervisorLocations(day);
        this.selectedDay = date;
    }

    onCheckClicked(check: Check) {
        for (const item of this.vehicleCkecks) {
            if (item.VehicleCheckID === check.VehicleCheckID) {
                item.IsSelected = !item.IsSelected;
                item.ZIndex = 1001;
            } else {
                item.IsSelected = false;
                item.ZIndex = 1;
            }
        }

        this.latitude = check.Latitude;
        this.longitude = check.Longitude;
        this.zoom = 20;
    }

    onInfoWindowClose(check: Check) {
        check.IsSelected = false;

        let isAnyItemSelected = false;
        for (const vehicleCheck of this.vehicleCkecks) {
            if (vehicleCheck.IsSelected) {
                isAnyItemSelected = true;
            }
        }

        if (!isAnyItemSelected) {
            this.setMapPosition(this.supervisorLocations);
        }
    }
    //#endregion

    //#region Methods used to process data into information
    setMapPosition(locations: Array<Check>) {
        if (locations.length <= 0) {
            return;
        }

        let minX, maxX, minY, maxY;

        // init first point
        ((point) => {
            minX = point.Latitude;
            maxX = point.Latitude;
            minY = point.Longitude;
            maxY = point.Longitude;
        })(locations[0]);

        // calculate rect
        locations.map((point) => {
            minX = Math.min(minX, point.Latitude);
            maxX = Math.max(maxX, point.Latitude);
            minY = Math.min(minY, point.Longitude);
            maxY = Math.max(maxY, point.Longitude);
        });

        const midX = (minX + maxX) / 2;
        const midY = (minY + maxY) / 2;
        const midPoint = [midX, midY];

        const deltaX = (maxX - minX);
        const deltaY = (maxY - minY);

        const region = {
            lat: midX,
            lng: midY,
            latitudeDelta: deltaX,
            longitudeDelta: deltaY,
        };

        const zoom = Math.round(Math.log(360 / region.longitudeDelta) / Math.LN2);

        this.latitude = region.lat;
        this.longitude = region.lng;
        this.zoom = zoom - 1;
    }

    compareDate(checkDate: Date, locationDate: Date) {
        checkDate = new Date(checkDate);
        checkDate.setMilliseconds(0);
        locationDate = new Date(locationDate);
        locationDate.setMilliseconds(0);

        if (checkDate.getTime() === locationDate.getTime()) {
            return true;
        } else {
            return false;
        }
    }

    getLocationIcon(location: Check) {
        if (location.IsValid === true && !location.IsSelected) {
            return '../../../assets/pin/location-ok.png';
        } else if (location.IsValid === true && location.IsSelected) {
            return '../../../assets/pin/location-ok-selected.png';
        } else if (location.IsValid === false && !location.IsSelected) {
            return '../../../assets/pin/location-fined.png';
        } else if (location.IsValid === false && location.IsSelected) {
            return '../../../assets/pin/location-fined-selected.png';
        }
    }
    //#endregion

    //#region pagination, search
    updateFilterTags(tags: Array<string>) {
        this.tags = tags;
        this.refreshFilteredList();
    }

    search(word?: string): void {
        this.searchWord = word;
        this.refreshFilteredList();
    }

    refreshFilteredList(): void {
        this.filteredList = this.vehicleCkecks;
        // this.filteredList = this.paginationService.filterArray(this.filteredList, 'Parking', this.tags);
        this.filteredList = this.paginationService.filterArray(this.filteredList, 'Plate', [this.searchWord]);
        this.setPage(1);
    }

    setPage(page: number): void {
        this.pager = this.paginationService.getPager(this.filteredList.length, page, 6);
        this.pagedItems = this.filteredList.slice(this.pager.startIndex, this.pager.endIndex + 1);
    }
    //#endregion
}
